import { useCallback } from 'react';

import type { DeserializedUser } from '@api';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useTagManager } from '@shared/common/providers/GoogleTagManagerProvider';
import { useFeatureToggles } from '@shared/hooks/useFeatureToggles';
import useLoginWithGivenWallet from '@shared/hooks/useLoginWithGivenWallet';
import { useRegisterQueryParams } from '@shared/hooks/useRegisterQueryParams';

type UserInfo = { isNewUser: boolean; user: DeserializedUser };

type LoginProps = {
  signMessage: string;
  walletType?: 'Passkey' | 'WalletConnect2';
  shouldReconnect?: boolean;
  onLoginCallback?: (userInfo: UserInfo) => void;
};

export const useLogin = () => {
  const { logout } = useAuth();
  const featureToggles = useFeatureToggles();
  const addQueryRegister = useRegisterQueryParams();

  const {
    mutateAsync: loginWithWallet,
    isPending,
    variables,
  } = useLoginWithGivenWallet({
    onLogout: logout,
  });
  const isLoggingInWithPasskey = isPending && variables?.signProps.walletType === 'Passkey';
  const isLoggingInWithWalletConnect =
    isPending && (!featureToggles.passkeyAuth || variables?.signProps.walletType === 'EVM');

  const { triggerAuthEvent } = useTagManager();

  const login = useCallback(
    async ({ walletType = 'WalletConnect2', shouldReconnect, onLoginCallback }: LoginProps) => {
      const userInfo = await loginWithWallet({
        signProps: {
          walletType,
          signType: 'SIWE',
          message: 'Confirm authorization in Magic Store with your wallet',
          shouldVerifyChain: true,
          shouldReconnect,
        },
      });

      if (userInfo) {
        triggerAuthEvent(userInfo.user.id, {
          isNewUser: userInfo.isNewUser,
        });

        if (userInfo.isNewUser) {
          addQueryRegister({
            loginMethod: walletType,
          });
        }

        if (onLoginCallback) {
          onLoginCallback(userInfo);
        }
      }
    },
    [loginWithWallet, triggerAuthEvent],
  );

  return {
    login,
    isLogging: isPending,
    isLoggingInWithPasskey,
    isLoggingInWithWalletConnect,
  };
};
